import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { TOOLTIPS_TEXT } from '../../../../../lib/constants';
import InfoPopup from '../../../../InfoPopup';
import SpinnerCircles from '../../../../Spinner/SpinerCircles';
import Toggle from '../../../../Toggle';
import { getPermissionForComponent } from '../../../../../utils/permissionsUtils';
import { useUnifiedFetch } from '../../../../../hooks/useUnifiedFetch';
import { useUnifiedMutate } from '../../../../../hooks/useUnifiedMutate';

const USER_FAILURE =
  'The IPv6 option could not be updated for the selected device.';
const USER_UNAVAILABLE =
  'The IPv6 option cannot be updated for the selected device.';
const IPV4_PATTERN =
  /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;

const hasValidDeviceIp = vpnAddress => {
  if (!vpnAddress || typeof vpnAddress !== 'string') return false;
  return IPV4_PATTERN.test(vpnAddress.split('/')[0].trim());
};

const Ipv6OptionToggle = ({
  boxId,
  vpnAddress,
  onDeviceLocked,
}) => {
  const user = useSelector(state => state.AppState.user);
  const isBoxTab = useSelector(state => state?.UIState?.globals?.isBoxTab);
  const permission = getPermissionForComponent('ipv6Option', user.permissionsGroup);
  const canWrite = permission === 'write' || permission === 'both';
  const canRead = permission === 'read' || canWrite;
  const deviceIpReady = hasValidDeviceIp(vpnAddress);

  const [enabled, setEnabled] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [statusError, setStatusError] = useState('');
  const requestBoxIdRef = useRef(null);

  const { isLoading, fetchData } = useUnifiedFetch(
    boxId ? `boxes/ipv6-option/${boxId}` : null,
    false
  );
  const { mutate, isLoading: isUpdating } = useUnifiedMutate();

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      if (!canRead || !boxId) {
        return;
      }
      if (!deviceIpReady) {
        setUnavailable(true);
        setStatusError(USER_UNAVAILABLE);
        return;
      }
      requestBoxIdRef.current = boxId;
      const result = await fetchData();
      if (cancelled || requestBoxIdRef.current !== boxId) {
        return;
      }
      if (result?.success && typeof result.enabled === 'boolean') {
        setEnabled(result.enabled);
        setUnavailable(false);
        setStatusError('');
      } else {
        setUnavailable(true);
        setStatusError(USER_UNAVAILABLE);
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [boxId, canRead, deviceIpReady, fetchData]);

  if (!canRead) {
    return null;
  }

  const isBusy = isLoading || isUpdating;
  const toggleDisabled = !canWrite || isBusy || !boxId || !deviceIpReady;

  const onToggle = async () => {
    if (toggleDisabled) {
      return;
    }
    const lockEl = document.getElementById('test-time-remaining');
    if (lockEl && lockEl.hidden) {
      onDeviceLocked && onDeviceLocked();
      return;
    }

    const nextEnabled = !enabled;
    const previousEnabled = enabled;
    const targetBoxId = boxId;
    requestBoxIdRef.current = targetBoxId;
    setStatusError('');

    const result = await mutate({
      endpoint: `boxes/ipv6-option/${targetBoxId}`,
      method: 'PUT',
      body: { enabled: nextEnabled, boxId: targetBoxId, isBoxTab },
    });

    if (requestBoxIdRef.current !== targetBoxId) {
      return;
    }

    if (result && result.success) {
      setEnabled(typeof result.enabled === 'boolean' ? result.enabled : nextEnabled);
      setUnavailable(false);
      setStatusError('');
      return;
    }

    setEnabled(previousEnabled);
    setStatusError(USER_FAILURE);
  };

  return (
    <Row className="mb-2 align-items-center">
      <Col sm={6} md={4} id="test-enable-ipv6" className="type-header d-flex align-items-center">
        Enable IPv6
        <sup>
          <InfoPopup id="enable-ipv6" text={TOOLTIPS_TEXT.ENABLE_IPV6} />
        </sup>
      </Col>
      <Col sm={6} className="ipv6-option-control">
        <div
          role="switch"
          aria-checked={enabled}
          aria-label="Enable IPv6"
          aria-disabled={toggleDisabled}
          aria-busy={isBusy}
        >
          <Toggle
            id="ipv6-option-toggle"
            disabled={toggleDisabled}
            value={enabled}
            customDisableText={
              !canWrite
                ? 'You do not have sufficient permissions to perform this function.'
                : USER_UNAVAILABLE
            }
            onToggle={onToggle}
          />
        </div>
        <span className="ipv6-option-status">
          {isBusy && <SpinnerCircles red />}
          {!deviceIpReady || unavailable
            ? 'Unavailable'
            : isUpdating
              ? 'Updating…'
              : enabled
                ? 'Enabled'
                : 'Disabled'}
        </span>
      </Col>
      {statusError && (
        <Col sm={12}>
          <span style={{ fontSize: '12px', color: '#dc3545' }}>{statusError}</span>
        </Col>
      )}
    </Row>
  );
};

export default Ipv6OptionToggle;
